'use client'
import Link from 'next/link'
import React from 'react'

import { motion } from 'framer-motion'
import { Globe, Heart, Mail, Phone, Star, Target, Users, Zap } from 'lucide-react'

import { cn } from '../../lib/utils'
import { AboutProps } from '../../types'
import { Card, CardContent } from '../card'
import { CornerBrackets } from '../kinetic/CornerBrackets'
import { KineticAboutContactHero } from '../kinetic/KineticAboutContactHero'
import { KineticMissionMarkPanel } from '../kinetic/KineticDecorativeLayers'
import { Container } from '../layout/container'
import { Section } from '../layout/section'
import { Button } from '../ui/button'

const darkTheme = {
  bg: 'bg-kinetic-surface',
  surface: 'bg-kinetic-surface-container-low',
  surfaceBorder: 'border-kinetic-outline-variant/30',
  text: 'text-kinetic-on-surface',
  textMuted: 'text-kinetic-on-surface-variant',
  textDim: 'text-kinetic-outline',
  accent: 'text-kinetic-primary-container',
  iconBg: 'bg-kinetic-primary-container',
  cardBg: 'bg-kinetic-surface-container-low border border-kinetic-outline-variant/30',
}

const iconMap = {
  Users,
  Globe,
  Star,
  Target,
  Heart,
  Zap,
}

type ValueAccent = {
  label: string
  heading: string
  bar: string
  icon: string
  iconBorder: string
}

function valueAccent(icon: keyof typeof iconMap): ValueAccent {
  switch (icon) {
    case 'Zap':
      return {
        label: 'text-kinetic-primary-container',
        heading: 'text-kinetic-primary-container',
        bar: 'bg-kinetic-primary-container',
        icon: 'text-kinetic-primary-container',
        iconBorder: 'border-kinetic-primary-container/40',
      }
    case 'Star':
      return {
        label: 'text-kinetic-outline',
        heading: 'text-kinetic-on-surface',
        bar: 'bg-kinetic-outline',
        icon: 'text-kinetic-on-surface',
        iconBorder: 'border-kinetic-outline-variant/50',
      }
    case 'Target':
    case 'Heart':
    case 'Users':
    default:
      return {
        label: 'text-kinetic-secondary-fixed',
        heading: 'text-kinetic-secondary-fixed',
        bar: 'bg-kinetic-secondary-fixed',
        icon: 'text-kinetic-secondary-fixed',
        iconBorder: 'border-kinetic-secondary-fixed/40',
      }
  }
}

function ValueCardIcon({
  iconName,
  accent,
  isDark,
}: {
  iconName: keyof typeof iconMap
  accent: ValueAccent
  isDark: boolean
}) {
  const Icon = iconMap[iconName]
  if (!Icon) return null

  return (
    <div className="mb-6 flex justify-center">
      <div
        className={cn(
          'relative flex h-16 w-16 items-center justify-center transition-transform duration-500 group-hover:scale-105',
          isDark
            ? cn('bg-kinetic-surface-container-lowest/90 rounded-none border', accent.iconBorder)
            : 'rounded-2xl bg-gradient-to-br from-shuttle-red to-shuttle-purple shadow-lg'
        )}
      >
        <Icon
          className={cn('h-8 w-8', isDark ? accent.icon : 'text-white')}
          strokeWidth={isDark ? 1.5 : 2}
          aria-hidden
        />
        {isDark ? <CornerBrackets className="opacity-40" /> : null}
      </div>
    </div>
  )
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
    },
  },
}

export const About = ({
  heroTitle,
  heroDescription,
  teamTitle,
  employees,
  valuesTitle,
  values,
  color = 'default',
}: AboutProps) => {
  const isDark = color === 'dark'
  return (
    <div
      className={`min-h-screen ${isDark ? darkTheme.bg : 'bg-gradient-to-br from-shuttle-navy via-shuttle-purple to-shuttle-navy'}`}
    >
      {/* Hero Section */}
      {isDark ? (
        <KineticAboutContactHero
          statusLabel="ENTITY_ID: CLOUD_SHUTTLE_AU"
          title={heroTitle}
          subtitle={heroDescription}
          afterSubtitle={
            <div className="mt-10 font-mono text-xs leading-relaxed text-kinetic-outline">
              <div>LOC_REF: 34.4280° S, 150.8930° E // UOW Innovation Campus, North Wollongong NSW</div>
              <div className="mt-1">Sydney base: Stone &amp; Chalk Tech Central, Haymarket</div>
              <div className="mt-1 text-kinetic-secondary-fixed-dim">HQ: North Wollongong (4.772 Google reviews)</div>
            </div>
          }
        />
      ) : (
        <Section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-shuttle-navy/90 to-shuttle-purple/90" />
          <div className="absolute inset-0">
            <div className="absolute inset-0 animate-pulse bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />
          </div>
          <Container className="relative z-10 py-24 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="mb-6 bg-gradient-to-r from-white via-shuttle-red to-white bg-clip-text text-6xl font-bold text-transparent md:text-7xl">
                {heroTitle}
              </h1>
              <p className="mx-auto max-w-4xl text-xl leading-relaxed text-gray-200 md:text-2xl">
                {heroDescription}
              </p>
            </motion.div>

            {/* Floating particles */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute h-2 w-2 rounded-full bg-shuttle-red opacity-60"
                  style={{
                    left: `${20 + i * 15}%`,
                    top: `${30 + (i % 2) * 40}%`,
                  }}
                  animate={{
                    y: [-20, 20, -20],
                    opacity: [0.3, 0.8, 0.3],
                  }}
                  transition={{
                    duration: 3 + i,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </div>
          </Container>
        </Section>
      )}

      {/* Mission Section */}
      {isDark && (
        <Section className="bg-kinetic-surface-container-low">
          <Container className="py-20">
            <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-12">
              <div className="relative md:col-span-5">
                <KineticMissionMarkPanel />
              </div>

              <div className="md:col-span-7">
                <h2 className="mb-6 font-headline text-4xl font-bold uppercase tracking-tight text-white md:text-5xl">
                  Our Mission
                </h2>
                <div className="space-y-8">
                  <div className="border-l-4 border-kinetic-secondary-fixed pl-6">
                    <h3 className="mb-2 font-headline text-xl font-bold text-kinetic-secondary-fixed">
                      Community &amp; craft
                    </h3>
                    <p className="leading-relaxed text-kinetic-on-surface-variant">
                      We grow independent data engineering communities across Australia and New
                      Zealand — meetups, workshops, and DataEngBytes — so practitioners have a place
                      to learn and belong.
                    </p>
                  </div>
                  <div className="border-l-4 border-kinetic-primary-container pl-6">
                    <h3 className="mb-2 font-headline text-xl font-bold text-kinetic-primary-container">
                      Delivery that lasts
                    </h3>
                    <p className="leading-relaxed text-kinetic-on-surface-variant">
                      We help clients ship data platforms, pipelines, and AI evaluation practice
                      with the same care we bring to community programmes — clear ownership, open
                      tooling where it fits, and outcomes you can defend in production.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </Section>
      )}

      {/* Team Section */}
      <Section className={isDark ? darkTheme.bg : 'bg-white'}>
        <Container className="py-20">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div
              className={
                isDark
                  ? 'border-kinetic-outline-variant/30 mb-12 flex flex-col gap-6 border-b pb-8 md:flex-row md:items-end md:justify-between'
                  : 'mb-16 text-center'
              }
              variants={itemVariants}
            >
              <div>
                <h2
                  className={cn(
                    'font-headline text-4xl font-bold tracking-tight md:text-5xl',
                    isDark
                      ? 'text-white'
                      : 'mb-4 bg-gradient-to-r from-shuttle-navy via-shuttle-purple to-shuttle-red bg-clip-text text-transparent'
                  )}
                >
                  {teamTitle}
                </h2>
                {isDark ? (
                  <p className="mt-2 font-mono text-xs uppercase tracking-[0.3em] text-kinetic-secondary-fixed-dim">
                    team_roster.json
                  </p>
                ) : (
                  <div className="mx-auto mt-4 h-1 w-24 bg-gradient-to-r from-shuttle-red to-shuttle-purple" />
                )}
              </div>
              {isDark ? (
                <div className="hidden text-right font-mono text-[10px] uppercase leading-relaxed text-kinetic-outline md:block">
                  Precision grade: Tier-1
                  <br />
                  Session: CS_CORE
                </div>
              ) : null}
            </motion.div>

            <motion.div
              className={cn(
                'grid grid-cols-1 gap-8 md:grid-cols-2',
                employees.length >= 4 ? 'lg:grid-cols-2 xl:grid-cols-4' : 'lg:grid-cols-3'
              )}
              variants={containerVariants}
            >
              {employees.map((employee, empIndex) => (
                <motion.div key={employee.name} variants={itemVariants}>
                  <Card
                    className={cn(
                      'group overflow-hidden transition-all duration-500 magnetic-hover',
                      isDark
                        ? 'bg-kinetic-surface-container-highest/60 hover:border-kinetic-primary-container/30 rounded-none border border-white/5 shadow-none backdrop-blur-[20px]'
                        : 'border-0 bg-white shadow-lg hover:shadow-2xl'
                    )}
                  >
                    <div className="relative">
                      {isDark ? (
                        <span className="text-kinetic-primary-container/60 absolute right-3 top-3 z-10 font-mono text-[10px]">
                          MEM_{String(empIndex + 1).padStart(2, '0')}
                        </span>
                      ) : null}
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={employee.image || '/api/placeholder/300/300'}
                          alt={employee.name}
                          className="h-full w-full transform object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      </div>
                      {!isDark && (
                        <div className="absolute inset-0 bg-gradient-to-t from-shuttle-navy/80 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                      )}
                      <div className="absolute bottom-4 left-4 right-4 translate-y-8 transform opacity-0 transition-transform duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                        <div className="flex space-x-2">
                          <Button
                            size="default"
                            variant="ghost"
                            className="text-white hover:bg-white/20"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button
                            size="default"
                            variant="ghost"
                            className="text-white hover:bg-white/20"
                          >
                            <Phone className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <CardContent
                      className={cn(
                        'p-6',
                        isDark
                          ? 'bg-kinetic-surface-container-low/90'
                          : 'bg-gradient-to-br from-white to-gray-50'
                      )}
                    >
                      <div className="space-y-4">
                        <div>
                          <h3
                            className={`mb-1 font-headline text-xl font-bold transition-colors duration-300 ${isDark ? darkTheme.text : 'text-shuttle-navy group-hover:text-shuttle-red'}`}
                          >
                            {employee.name}
                          </h3>
                          <p
                            className={`font-semibold ${isDark ? darkTheme.accent : 'text-shuttle-purple'}`}
                          >
                            {employee.role}
                          </p>
                        </div>
                        <p
                          className={`line-clamp-4 text-sm leading-relaxed ${isDark ? darkTheme.textMuted : 'text-gray-600'}`}
                        >
                          {employee.bio}
                        </p>
                        <div
                          className={`space-y-2 border-t pt-4 ${
                            isDark ? 'border-kinetic-outline-variant/30' : 'border-gray-100'
                          }`}
                        >
                          <a
                            href={`mailto:${employee.email}`}
                            className={`group/link flex items-center text-sm transition-colors ${
                              isDark
                                ? 'text-kinetic-on-surface-variant hover:text-kinetic-primary-container'
                                : 'text-gray-600 hover:text-shuttle-red'
                            }`}
                          >
                            <Mail className="mr-2 h-4 w-4 group-hover/link:animate-bounce" />
                            <span className="truncate">{employee.email}</span>
                          </a>
                          <a
                            href={`tel:${employee.phone}`}
                            className={`group/link flex items-center text-sm transition-colors ${
                              isDark
                                ? 'text-kinetic-on-surface-variant hover:text-kinetic-primary-container'
                                : 'text-gray-600 hover:text-shuttle-red'
                            }`}
                          >
                            <Phone className="mr-2 h-4 w-4 group-hover/link:animate-bounce" />
                            <span>{employee.phone}</span>
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      {/* Values Section */}
      <Section
        className={
          isDark
            ? 'border-kinetic-outline-variant/30 border-t bg-kinetic-surface-container-low'
            : 'bg-gradient-to-br from-gray-50 to-white'
        }
      >
        <Container className="py-20">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div
              className={
                isDark
                  ? 'border-kinetic-outline-variant/30 mb-12 flex flex-col gap-4 border-b pb-8 md:flex-row md:items-end md:justify-between'
                  : 'mb-16 text-center'
              }
              variants={itemVariants}
            >
              <div>
                <h2
                  className={`font-headline text-4xl font-bold tracking-tight md:text-5xl ${
                    isDark
                      ? 'text-white'
                      : 'bg-gradient-to-r from-shuttle-navy via-shuttle-purple to-shuttle-red bg-clip-text text-transparent'
                  } ${!isDark ? 'mb-4' : ''}`}
                >
                  {valuesTitle}
                </h2>
                {isDark ? (
                  <p className="mt-2 font-mono text-xs uppercase tracking-[0.3em] text-kinetic-outline">
                    SYS_RELIABILITY_V3
                  </p>
                ) : (
                  <div className="mx-auto mt-4 h-1 w-24 bg-gradient-to-r from-shuttle-red to-shuttle-purple" />
                )}
              </div>
              {isDark ? (
                <span className="font-mono text-[10px] uppercase tracking-widest text-kinetic-outline md:text-right">
                  MOD_SET · 03
                </span>
              ) : null}
            </motion.div>

            <motion.div
              className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-3"
              variants={containerVariants}
            >
              {values.map((value, vi) => {
                const idx = String(vi + 1).padStart(2, '0')
                const accent = valueAccent(value.icon)

                return (
                  <motion.div key={value.title} variants={itemVariants}>
                    <div
                      className={cn(
                        'group relative overflow-hidden p-8 text-center transition-all duration-500 magnetic-hover',
                        isDark
                          ? 'bg-kinetic-surface-container-high transition-colors hover:bg-kinetic-surface-container-highest'
                          : 'rounded-2xl border border-gray-100 bg-white shadow-lg hover:shadow-2xl',
                        isDark && 'rounded-none'
                      )}
                    >
                      <ValueCardIcon iconName={value.icon} accent={accent} isDark={isDark} />
                      {isDark ? (
                        <>
                          <div className={cn('mb-3 font-mono text-sm', accent.label)}>
                            [{idx}] {value.title.toUpperCase()}
                          </div>
                          <h3
                            className={cn('mb-4 font-headline text-2xl font-bold', accent.heading)}
                          >
                            {value.title}
                          </h3>
                          <p className="text-sm leading-loose text-kinetic-on-surface-variant">
                            {value.description}
                          </p>
                          <div
                            className={cn(
                              'mt-8 h-1 w-0 transition-all duration-500 group-hover:w-full',
                              accent.bar
                            )}
                          />
                        </>
                      ) : (
                        <>
                          <h3 className="mb-4 font-headline text-2xl font-bold text-shuttle-navy transition-colors duration-300 group-hover:text-shuttle-red">
                            {value.title}
                          </h3>
                          <p className="text-lg leading-relaxed text-gray-600">
                            {value.description}
                          </p>
                        </>
                      )}
                    </div>
                  </motion.div>
                )
              })}
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section
        className={
          isDark
            ? 'border-kinetic-outline-variant/30 border-t bg-kinetic-surface'
            : 'bg-gradient-to-r from-shuttle-navy via-shuttle-purple to-shuttle-navy'
        }
      >
        <Container className="py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className={`mb-6 font-headline text-4xl font-bold md:text-5xl ${isDark ? darkTheme.text : 'text-white'}`}
            >
              {isDark ? 'Ready to talk delivery?' : 'Ready to Get Started?'}
            </h2>
            <p
              className={`mx-auto mb-8 max-w-2xl text-xl ${isDark ? darkTheme.textMuted : 'text-gray-200'}`}
            >
              {isDark
                ? 'Tell us about your data platform, pipelines, or AI evaluation goals. We usually reply within one business day from our HQ at UOW Innovation Campus, North Wollongong NSW (or Stone & Chalk, Haymarket when in Sydney).'
                : "Let's discuss how we can help transform your data infrastructure and accelerate your business growth."}
            </p>
            <Link href="/contact-us">
              <Button
                size="xl"
                variant={isDark ? 'kinetic' : 'default'}
                className={
                  isDark
                    ? 'px-8 py-4 text-base shadow-lg hover:shadow-[0_0_20px_rgba(0,240,255,0.35)]'
                    : 'rounded-xl bg-shuttle-red px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:bg-shuttle-red/90 hover:shadow-xl'
                }
              >
                {isDark ? 'Contact us' : 'Get In Touch'}
              </Button>
            </Link>
            {isDark && (
              <div className="mx-auto mt-8 max-w-2xl space-y-2 text-left font-mono text-[11px] uppercase tracking-[0.18em] text-kinetic-outline">
                <div className="border-kinetic-outline-variant/30 flex justify-between border-b pb-2">
                  <span>Response target</span>
                  <span className="text-kinetic-secondary-fixed">1 business day</span>
                </div>
                <div className="border-kinetic-outline-variant/30 flex justify-between border-b pb-2">
                  <span>Region</span>
                  <span className="text-kinetic-primary-container">AU · NZ</span>
                </div>
                <div className="border-kinetic-outline-variant/30 flex justify-between border-b pb-2">
                  <span>Channel</span>
                  <span className="text-white">info@cloudshuttle.com.au</span>
                </div>
              </div>
            )}
          </motion.div>
        </Container>
      </Section>

      {isDark && (
        <footer className="border-kinetic-outline-variant/30 border-t bg-kinetic-surface py-8">
          <Container>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <span className="font-headline text-base font-bold text-white">Cloud Shuttle</span>
                <p className="mt-1 text-sm text-kinetic-on-surface-variant">
                  Empowering the next generation of data professionals.
                </p>
              </div>
              <div className="flex gap-6">
                <Link
                  href="/services"
                  className="text-sm text-kinetic-on-surface-variant hover:text-kinetic-on-surface"
                >
                  Services
                </Link>
                <Link
                  href="/workshops"
                  className="text-sm text-kinetic-on-surface-variant hover:text-kinetic-on-surface"
                >
                  Workshops
                </Link>
                <Link
                  href="/contact-us"
                  className="text-sm text-kinetic-on-surface-variant hover:text-kinetic-on-surface"
                >
                  Contact
                </Link>
              </div>
            </div>
            <p className="mt-4 font-mono text-[10px] text-sm uppercase tracking-wider text-kinetic-on-surface-variant">
              © {new Date().getFullYear()} Cloud Shuttle. All rights reserved.
            </p>
          </Container>
        </footer>
      )}
    </div>
  )
}
